import Litepicker from "litepicker";
import "litepicker/dist/css/litepicker.css";

/**
 * Moteur de réservation (bloc recherche + champs conducteur).
 *
 * Dates départ / retour : deux Litepicker coexistent sur les mêmes inputs.
 * - startPicker : une seule date (départ), affiche le retour déjà connu en surbrillance.
 * - returnPicker : plage départ→retour ; au clic on ne change que le retour (départ figé).
 * Un seul calendrier visible à la fois (openStartPicker / openEndPicker).
 */
$(() => {
	if (!document.getElementById("date-start")) return;

	// ── Références DOM & état UI ─────────────────────────────────
	const isMobile = window.innerWidth < 768;

	const startEl = document.getElementById("date-start");
	const endEl = document.getElementById("date-end");
	const startControl = startEl.closest(".form-resa__control--date");
	const endControl = endEl.closest(".form-resa__control--date");
	/** Input actuellement « focus calendrier » (bordure .is-picker-active). */
	let activeDateElement = startEl;

	/** Active visuellement le champ départ ou retour (ou aucun si element === null). */
	const setActiveDateControl = (element) => {
		activeDateElement = element;
		startControl.classList.toggle("is-picker-active", element === startEl);
		endControl.classList.toggle("is-picker-active", element === endEl);
	};

	/** Chaîne DD/MM/YYYY → Date locale, ou null si invalide. */
	const dateFromInput = (value) => {
		const [day, month, year] = value.split("/").map(Number);
		return day && month && year ? new Date(year, month - 1, day) : null;
	};

	const inputVal = (el) => el.value.trim();

	/** Base partagée ; returnPicker écrase singleMode, elementEnd, allowRepick, etc. */
	const pickerOptions = {
		singleMode: true,
		numberOfMonths: isMobile ? 1 : 2,
		numberOfColumns: isMobile ? 1 : 2,
		format: "DD/MM/YYYY",
		lang: "fr-FR",
		autoApply: true,
		minDate: new Date(new Date().setHours(0, 0, 0, 0)),
		scrollToDate: true,
		resetButton: true,
	};

	// ── Helpers dates ─────────────────────────────────────────────

	/** Clic sur l’input départ ou l’icône calendrier départ. */
	const isStartClick = (target) =>
		target === startEl ||
		(target instanceof Element && target.closest(".litepicker-trigger-start"));

	/** Marque le jour de retour dans le calendrier départ (.is-return-date, stylé en SCSS). */
	const markReturnDay = (container, time) => {
		if (!time || !container) return;
		container
			.querySelectorAll(`.day-item[data-time="${time}"]`)
			.forEach((day) => day.classList.add("is-return-date"));
	};

	/**
	 * Jours à surligner (API highlightedDays) entre départ et retour inclus.
	 * Si seul le retour est connu, une seule date est renvoyée.
	 */
	const datesBetweenDepartureAndReturn = (departure, returnDate) => {
		if (!returnDate) return [];
		const [first, last] = departure
			? [departure, returnDate].sort((a, b) => a - b)
			: [returnDate, returnDate];
		const dates = [];
		for (const d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) {
			dates.push(new Date(d));
		}
		return dates;
	};

	/** Re-render chaque cellule du calendrier (détail interne Litepicker). */
	const refreshDayCells = (picker) =>
		picker.ui.querySelectorAll(".day-item").forEach((cell) => {
			const date = new picker.DateTime(Number(cell.dataset.time));
			cell.className = picker.renderDay(date).className;
		});

	/** Picker retour : désactive la prévisualisation de plage au survol des jours. */
	const resetRangePreviewOnHover = (picker) => {
		picker.on("render:month", (month) => {
			month.querySelectorAll(".day-item").forEach((day) => {
				day.addEventListener("mouseenter", () => {
					picker.datePicked.length = 0;
					refreshDayCells(picker);
				});
			});
		});
	};

	let returnPicker;

	/**
	 * Phase capture : returnPicker écoute aussi #date-start (mode plage).
	 * On intercepte tôt pour toujours lancer le flux départ (startPicker).
	 */
	document.addEventListener(
		"click",
		(e) => {
			if (!isStartClick(e.target)) return;
			e.stopImmediatePropagation();
			openStartPicker();
		},
		true,
	);

	// ── Litepicker départ (date unique) ───────────────────────────
	const startPicker = new Litepicker({
		...pickerOptions,
		element: startEl,
		startDate: inputVal(startEl) || undefined,
		setup(picker) {
			picker.on("render:month", (month) =>
				markReturnDay(month, endEl.dataset.returnHighlightTime),
			);
			picker.on("show", () => setActiveDateControl(startEl));
			picker.on("hide", () => {
				if (activeDateElement === startEl) setActiveDateControl(null);
			});
			picker.on("selected", (date) => {
				if (!date) return;
				const departure = date.toJSDate();
				const keptReturn = dateFromInput(inputVal(endEl));
				// Retour conservé s’il est encore ≥ départ, sinon même jour que le départ.
				const returnDate =
					keptReturn && keptReturn >= departure ? keptReturn : departure;
				// Synchronise les deux champs HTML via l’instance plage (sans l’afficher).
				returnPicker.setDateRange(departure, returnDate);
				// Tick suivant : basculer sur le calendrier retour.
				setTimeout(openEndPicker, 0);
			});
		},
	});

	// ── Litepicker retour (plage, départ verrouillé au clic) ──────
	returnPicker = new Litepicker({
		...pickerOptions,
		element: startEl,
		elementEnd: endEl,
		singleMode: false,
		allowRepick: true,
		selectForward: true,
		showTooltip: false,
		startDate: inputVal(startEl) || inputVal(endEl) || undefined,
		endDate: inputVal(endEl) || undefined,
		setup(picker) {
			// Sans ça, un clic relancerait une nouvelle plage depuis le jour cliqué.
			picker.on("before:click", (target) => {
				if (!target.classList?.contains("day-item")) return;
				const departure = picker.getStartDate();
				if (departure) picker.datePicked = [departure];
			});
			resetRangePreviewOnHover(picker);
			picker.on("show", (el) => setActiveDateControl(el || endEl));
			picker.on("hide", () => {
				if (activeDateElement === endEl || activeDateElement === startEl) {
					setActiveDateControl(null);
				}
			});
			picker.on("selected", (start, end) => {
				if (start && end) picker.hide();
			});
		},
	});

	// ── Ouverture calendriers ─────────────────────────────────────

	/** Prépare surbrillance retour + mois visible, puis affiche startPicker. */
	function openStartPicker() {
		returnPicker.hide();
		setActiveDateControl(startEl);
		const departure = dateFromInput(inputVal(startEl));
		const returnDate = dateFromInput(inputVal(endEl));
		// Mémorisé pour markReturnDay (data-time = getTime() du jour retour).
		endEl.dataset.returnHighlightTime = returnDate ? String(returnDate.getTime()) : "";
		startPicker.setHighlightedDays(
			datesBetweenDepartureAndReturn(departure, returnDate),
		);
		const visible = departure || returnDate;
		if (visible) startPicker.gotoDate(visible);
		startPicker.show(startEl);
		markReturnDay(startPicker.ui, endEl.dataset.returnHighlightTime);
	}

	/** Affiche returnPicker ancré sur le champ retour. */
	function openEndPicker() {
		startPicker.hide();
		setActiveDateControl(endEl);
		returnPicker.show(endEl);
	}

	/** Branche clic + Entrée/Espace sur les boutons déclencheurs. */
	const bindTrigger = (selector, open) =>
		document.querySelectorAll(selector).forEach((btn) => {
			btn.addEventListener("click", open);
			btn.addEventListener("keydown", (e) => {
				if (e.key !== "Enter" && e.key !== " ") return;
				e.preventDefault();
				open();
			});
		});

	startEl.addEventListener("click", openStartPicker);
	endEl.addEventListener("click", openEndPicker);
	bindTrigger(".litepicker-trigger-start", openStartPicker);
	bindTrigger(".litepicker-trigger-end", openEndPicker);

	// ── Select2 — lieux, âge & heures ─────────────────────────────
	const initSelect2 = (selector, extra = {}) =>
		$(selector).each(function () {
			$(this).select2({
				language: "fr",
				width: "100%",
				minimumResultsForSearch: -1,
				dropdownCssClass: "form-resa-dropdown",
				placeholder: $(this).data("placeholder") || "",
				...extra,
			});
		});

	initSelect2("#place-start, #age");

	// Lieu de retour : liste dans la modale (dropdownParent évite le clipping).
	initSelect2("#place-end", {
		dropdownParent: $("#js-return-modal .form-resa-modal__box"),
	});

	$("#hour-start, #hour-end").select2({
		language: "fr",
		width: "resolve",
		minimumResultsForSearch: -1,
		dropdownCssClass: "form-resa-dropdown form-resa-dropdown--hour",
	});

	// ── Modale « lieu de retour différent » ───────────────────────
	const $modal = $("#js-return-modal");
	const $trigger = $("#js-return-trigger");
	const $returnText = $("#js-return-text");
	const $returnRemove = $("#js-return-remove");
	const $closeBtn = $("#js-return-modal-close");
	const $finLieuId = $("#fin_lieu_id");
	const $placeEnd = $("#place-end");

	const setModalOpen = (isOpen) => {
		$modal.prop("hidden", !isOpen).attr("aria-hidden", String(!isOpen));
		(isOpen ? $closeBtn : $trigger).focus();
	};
	const closeReturnModal = () => setModalOpen(false);

	$trigger.on("click", (e) => {
		if (!$(e.target).closest("#js-return-remove").length) setModalOpen(true);
	});
	$("#js-return-modal-overlay").on("click", closeReturnModal);
	$closeBtn.on("click", closeReturnModal);

	$(document).on("keydown", (e) => {
		if (e.key === "Escape" && !$modal.prop("hidden")) closeReturnModal();
	});

	$("#js-return-modal-confirm").on("click", () => {
		const val = $placeEnd.val();
		if (val) {
			$finLieuId.val(val);
			$returnText.text($placeEnd.find("option:selected").text().trim());
			$returnRemove.removeAttr("hidden");
			$trigger.addClass("is-selected");
		}
		closeReturnModal();
	});

	$returnRemove.on("click", (e) => {
		e.stopPropagation();
		$placeEnd.val("").trigger("change");
		$returnText.text("Lieu de retour différent\u00a0?");
		$returnRemove.attr("hidden", "");
		$trigger.removeClass("is-selected");
	});
});

/**
 * Date de naissance et permis (étapes tunnel, hors bloc #date-start).
 * birthEl2 / licenceEl2 : présents sur certains formulaires, pickers à brancher si besoin.
 */
$(() => {
	const [birthEl, licenceEl, ...otherEls] = [
		"datepicker-birth",
		"datepicker-driver-licence",
		"datepicker-birth-2",
		"datepicker-driver-licence-2",
	].map((id) => document.getElementById(id));

	if (![birthEl, licenceEl, ...otherEls].some(Boolean)) return;

	const currentYear = new Date().getFullYear();

	const createPicker = (element, extra = {}) =>
		new Litepicker({
			singleMode: true,
			format: "DD/MM/YYYY",
			lang: "fr-FR",
			autoApply: true,
			maxDate: new Date(),
			scrollToDate: true,
			numberOfMonths: 1,
			numberOfColumns: 1,
			resetButton: false,
			dropdowns: {
				minYear: currentYear - 100, // jusqu'à 100 ans en arrière
				maxYear: currentYear,
				months: true,
				years: true,
			},
			element,
			...extra,
		});

	if (birthEl) {
		const viewDate = new Date();
		viewDate.setFullYear(viewDate.getFullYear() - 23);
		createPicker(birthEl, {
			setup(picker) {
				picker.on("show", () => {
					if (!picker.getStartDate()) picker.gotoDate(viewDate);
				});
			},
		});
	}
	if (licenceEl) createPicker(licenceEl);
});